import type { Node } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables';
import type { TableContext } from '@atlaskit/editor-tables/table-map';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { addRow } from '@atlaskit/editor-tables/utils';
import { fg } from '@atlaskit/platform-feature-flags';

import type {
	InsertNodeAfterCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';

import { findNodeByLocalId } from './utils/find-node-by-local-id';

export const createInsertNodeAfterCommand: StreamingCommandFactory<InsertNodeAfterCommand> =
	(api) => (command) => {
		api.core.actions.execute(({ tr }) => {
			findNodeByLocalId(api, tr, command.localId, ({ pos, node, parent }) => {
				if (node.type.name === 'tableRow' && parent) {
					const tableMap = TableMap.get(parent);
					const $pos = tr.doc.resolve(pos);
					const tableStart = $pos.start($pos.depth);
					const rect = tableMap.findCell(pos - tableStart + 1);
					const tableContext: TableContext = {
						map: tableMap,
						table: parent,
						tableStart,
					};
					addRow(tr, tableContext, rect.bottom);

					const $newPos = tr.doc.resolve(pos);
					const table = $newPos.node($newPos.depth);
					const newTableMap = TableMap.get(table);
					// Not considering merged cells
					const cells = newTableMap.mapByRow[rect.bottom];
					const selection = CellSelection.rowSelection(
						tr.doc.resolve(cells[0] + tableStart),
						tr.doc.resolve(cells[cells.length - 1] + tableStart),
					);
					tr.setSelection(selection).scrollIntoView();
				} else {
					let insertPos = Math.min(pos + node.nodeSize, tr.doc.content.size);

					if (fg('platform_editor_ai_aifc_patch_ga')) {
						insertPos = pos + node.nodeSize;

						while (insertPos < tr.doc.content.size) {
							const $pos = tr.doc.resolve(insertPos);
							let nextNode: Node | null = null;

							// For top-level nodes (depth 0 = document), check document children directly
							// For nested nodes, use nodeAfter which works at higher depths
							if ($pos.depth === 0) {
								// At document root level, find the next top-level child
								const childIndex = $pos.indexAfter(0);
								if (childIndex < tr.doc.childCount) {
									nextNode = tr.doc.child(childIndex);
								}
							} else {
								// For nested structures, nodeAfter works correctly
								nextNode = $pos.nodeAfter;
							}

							// Skip nodes without localId (from previous AI insertions)
							// This prevents sequential insertions from overriding each other
							if (nextNode && !nextNode.attrs.localId) {
								insertPos += nextNode.nodeSize;
							} else {
								break;
							}
						}
					}
					if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
						insertPos--;
					}
					tr.insert(insertPos, tr.doc.type.schema.nodes.paragraph.create());
					const selection = TextSelection.create(tr.doc, insertPos + 1);
					tr.setSelection(selection).scrollIntoView();
				}
			});
			return tr;
		});
		return true;
	};
